import type { MobileBFFGuide, Guide, Market } from '@/src/homepage/types/guides';
import { getPlaceFromLocationSlug } from '@/src/homepage/api/geo/getPlaceFromLocationSlug';

// The mobile BFF discard location data from guides-service since the native
// apps don't use it. Unfortunately, the webapp does, so this function re-hydrates
// the location data for the guide. This should be unnecessary once we have a
// dedicated bff for the webapp
export async function hydrateGuideLocation(
    guide: MobileBFFGuide,
    locationSlug: string,
): Promise<Guide> {
    const emptyLocationResponse = { ...guide, locations: [] };

    if (GUIDE_MARKETS_BY_LOCATION_SLUG[locationSlug]) {
        return {
            ...guide,
            locations: [GUIDE_MARKETS_BY_LOCATION_SLUG[locationSlug]],
        };
    }

    try {
        const place = await getPlaceFromLocationSlug(locationSlug);
        if (!place) {
            return emptyLocationResponse;
        }

        return {
            ...guide,
            locations: [
                {
                    // This `id` in this case isn't actually the `id` for the Market, but
                    // this has no implication on the webapp.
                    id: place.id,
                    displayName: place.name,
                    slug: place.location_slug || locationSlug,
                    placeId: place.id,
                },
            ],
        };
    } catch (error) {
        console.error(`Failed to get place for slug ${locationSlug}:`, error);
        return emptyLocationResponse;
    }
}

// There's a limited set of markets that can be assigned to a guide.
// This is meant to reduce the number of requests to geo service
// and reduce latency while we're reliant on the mobile BFF
// IMPORTANT! Keep in sync with edge-functions/src/redirect/openMarkets.ts
export const GUIDE_MARKETS_BY_LOCATION_SLUG: Record<string, Market> = {
    'ga--atlanta': {
        id: 'C6DBF74E-681E-49F3-93F4-59AB39D7DC62',
        displayName: 'Atlanta',
        slug: 'ga--atlanta',
        placeId: '85936429',
    },
    'ma--boston': {
        id: '426DE6BB-0198-4E0F-83A3-3A44E535B316',
        displayName: 'Boston',
        slug: 'ma--boston',
        placeId: '85950361',
    },
    'il--chicago': {
        id: 'C231B5E4-ADB7-4158-B9A1-E0931F434A17',
        displayName: 'Chicago',
        slug: 'il--chicago',
        placeId: '85940195',
    },
    'tx--dallas': {
        id: 'E981C20D-F97D-4FE5-9A53-29FF06D950E0',
        displayName: 'Dallas',
        slug: 'tx--dallas',
        placeId: '101724385',
    },
    'dc--washington': {
        id: '4918F128-D8B3-43CE-812D-93756C2AEC4C',
        displayName: 'DC',
        slug: 'dc--washington',
        placeId: '85931779',
    },
    'cuba--havana': {
        id: '1066D760-4A7E-4CA8-84EE-96CD74BF3B2E',
        displayName: 'Havana, Cuba (Test Location)',
        slug: 'cuba--havana',
        placeId: '1125939059',
    },
    'tx--houston': {
        id: 'F0461BA0-800C-4A05-AEFE-2E9DB8C566AA',
        displayName: 'Houston',
        slug: 'tx--houston',
        placeId: '101725629',
    },
    'united-kingdom--london': {
        id: '2CEE4470-FB1E-4345-8A64-3AB912569C76',
        displayName: 'London, England',
        slug: 'united-kingdom--london',
        placeId: '101750367',
    },
    'ca--los-angeles': {
        id: '1C06B49B-9F3D-4C1D-B896-38790A5EDDA8',
        displayName: 'Los Angeles ',
        slug: 'ca--los-angeles',
        placeId: '85923517',
    },
    'australia--melbourne': {
        id: '921CC844-50F5-4AE5-B17F-190C5E1E54DA',
        displayName: 'Melbourne, Australia',
        slug: 'australia--melbourne',
        placeId: '101933229',
    },
    'fl--miami': {
        id: 'BC8DCA71-6C93-49A3-858C-3F8D4E8779FA',
        displayName: 'Miami',
        slug: 'fl--miami',
        placeId: '85933669',
    },
    'tn--nashville': {
        id: 'DC3C9E59-39F9-4E78-9668-116F22BD5066',
        displayName: 'Nashville, TN',
        slug: 'tn--nashville',
        placeId: '101723183',
    },
    'ny--new-york': {
        id: 'C77E1558-42D1-4FF6-868C-7B1157EC891D',
        displayName: 'New York',
        slug: 'ny--new-york',
        placeId: '85977539',
    },
    'ca--san-francisco': {
        id: '5D98B14A-8644-43B9-A024-9BD4D2A7CE5B',
        displayName: 'San Francisco',
        slug: 'ca--san-francisco',
        placeId: '85922583',
    },
    'canada--toronto': {
        id: '3F8C67A2-8AF4-4E1C-8D7E-74888C0814C5',
        displayName: 'Toronto, Canada',
        slug: 'canada--toronto',
        placeId: '101735835',
    },
    'tx--austin': {
        id: '60509774-FAD3-4338-8664-342F725146A6',
        displayName: 'Austin, Texas',
        slug: 'tx--austin',
        placeId: '101724577',
    },
    'md--baltimore': {
        id: '5D0E490A-5F02-4366-A801-698217AF523B',
        displayName: 'Baltimore, Maryland',
        slug: 'md--baltimore',
        placeId: '85949461',
    },
    'nc--charlotte': {
        id: 'D72D3B80-1CE7-4241-9EB1-8DF13B618F66',
        displayName: 'Charlotte, North Carolina',
        slug: 'nc--charlotte',
        placeId: '85981333',
    },
    'co--denver': {
        id: '4A7B840E-C614-4A77-9AC9-E83158BB229B',
        displayName: 'Denver, Colorado',
        slug: 'co--denver',
        placeId: '102086135',
    },
    'mi--detroit': {
        id: 'AAEFE6B5-2F72-413D-97AB-855735B8CB81',
        displayName: 'Detroit, Michigan',
        slug: 'mi--detroit',
        placeId: '85951091',
    },
    'nv--las-vegas': {
        id: '7461176D-10CD-476E-82A6-7BD337D093C7',
        displayName: 'Las Vegas, Nevada',
        slug: 'nv--las-vegas',
        placeId: '85974801',
    },
    'united-kingdom--birmingham': {
        id: '6BFF1E65-6575-4C30-ABF8-A2CDD7D3B87B',
        displayName: 'Birmingham, England',
        slug: 'united-kingdom--birmingham',
        placeId: '101750467',
    },
    'ireland--dublin': {
        id: '76F5AB03-B047-4F34-9D68-F1FF423E8296',
        displayName: 'Dublin, Ireland',
        slug: 'ireland--dublin',
        placeId: '101751737',
    },
    'united-kingdom--manchester': {
        id: '1E7E5F76-2295-4068-9A71-A28F4600BDBD',
        displayName: 'Manchester, England',
        slug: 'united-kingdom--manchester',
        placeId: '101750525',
    },
    'pa--philadelphia': {
        id: '7682B1A4-4A0D-43D5-BA2A-06D7671F3913',
        displayName: 'Philadelphia, Pennsylvania',
        slug: 'pa--philadelphia',
        placeId: '101718083',
    },
    'australia--sydney': {
        id: '4F18EDCC-F6BC-47F3-A9F4-C867FBB9669D',
        displayName: 'Sydney, Australia',
        slug: 'australia--sydney',
        placeId: '101932003',
    },
};
